import { Braces } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface JsonDescEditorProps {
	value: Record<string, unknown> | null;
	onChange: (value: Record<string, unknown> | null) => void;
}

export default function JsonDescEditor({
	value,
	onChange,
}: JsonDescEditorProps) {
	const { t } = useTranslation("brands");
	const onChangeRef = useRef(onChange);
	onChangeRef.current = onChange;

	const isInternalChange = useRef(false);
	const textareaRef = useRef<HTMLTextAreaElement>(null);

	const [jsonText, setJsonText] = useState(() => formatJson(value));
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (isInternalChange.current) {
			isInternalChange.current = false;
			return;
		}
		setJsonText(formatJson(value));
		setError(null);
	}, [value]);

	const syncFromJson = useCallback(
		(text: string) => {
			const trimmed = text.trim();
			if (!trimmed) {
				isInternalChange.current = true;
				onChangeRef.current(null);
				setError(null);
				return;
			}
			try {
				const parsed = JSON.parse(trimmed);
				if (
					typeof parsed !== "object" ||
					Array.isArray(parsed) ||
					parsed === null
				) {
					setError(t("json_desc.must_be_object"));
					return;
				}
				isInternalChange.current = true;
				onChangeRef.current(parsed as Record<string, unknown>);
				setError(null);
			} catch {
				setError(t("json_desc.invalid"));
			}
		},
		[t],
	);

	const handleChange = (text: string) => {
		setJsonText(text);
		syncFromJson(text);
	};

	const handlePrettify = () => {
		try {
			const parsed = JSON.parse(jsonText);
			if (
				typeof parsed === "object" &&
				!Array.isArray(parsed) &&
				parsed !== null
			) {
				const formatted = JSON.stringify(parsed, null, 2);
				setJsonText(formatted);
				setError(null);
				isInternalChange.current = true;
				onChangeRef.current(parsed as Record<string, unknown>);
			}
		} catch {
			setError(t("json_desc.cannot_prettify"));
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
		if (e.key === "Tab") {
			e.preventDefault();
			const textarea = e.currentTarget;
			const start = textarea.selectionStart;
			const end = textarea.selectionEnd;
			const newText = `${jsonText.substring(0, start)}  ${jsonText.substring(end)}`;
			setJsonText(newText);
			syncFromJson(newText);
			requestAnimationFrame(() => {
				textarea.selectionStart = textarea.selectionEnd = start + 2;
			});
		}
	};

	return (
		<div className="space-y-1.5">
			<div className="flex items-center justify-between">
				<Label className="text-xs">JSON</Label>
				<Button
					type="button"
					variant="ghost"
					size="sm"
					className="h-auto py-1 text-xs"
					onClick={handlePrettify}
				>
					<Braces className="size-3.5" />
					{t("json_desc.prettify")}
				</Button>
			</div>
			<Textarea
				ref={textareaRef}
				placeholder='{ "description": "..." }'
				value={jsonText}
				onChange={(e) => handleChange(e.target.value)}
				onKeyDown={handleKeyDown}
				rows={6}
				className="field-sizing-content font-mono bg-input shadow-inner text-sm max-h-29.5 min-h-0 resize-none py-1.75 placeholder:text-muted-foreground/75"
			/>
			{error && <p className="text-xs text-destructive">{error}</p>}
		</div>
	);
}

function formatJson(obj: Record<string, unknown> | null): string {
	if (!obj) return "";
	const entries = Object.entries(obj);
	if (!entries.length) return "";
	return JSON.stringify(obj, null, 2);
}
